import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import {db} from '../firebase-config';
import {Alert, Box, Button, Container, LinearProgress, Typography} from '@mui/material';
import {FeatureGrid, LandingHero, OpenGames, QuizGrid, Testimonials} from '../components/home';
import '../styles/components/home.css';
import AddIcon from '@mui/icons-material/Add';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

const generateLobbyCode = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return code;
};

const Home = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [openGames, setOpenGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentLobby, setCurrentLobby] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Firebase-Initialisierung prüfen
  useEffect(() => {
    const checkFirebase = async () => {
      try {
        if (!db) {
          console.error('Firebase db ist nicht definiert');
          setError('Firebase nicht verfügbar');
          return;
        }

        if (!db.app?.options?.projectId) {
          console.log('Warte auf Firebase-Initialisierung...');
          return;
        }

        try {
          setFirebaseReady(true);
        } catch (err) {
          console.error('Firebase-Verbindungstest fehlgeschlagen:', err);
          setError('Firebase-Verbindung fehlgeschlagen');
        }
      } catch (err) {
        console.error('Firebase-Initialisierungsfehler:', err);
        setError('Firebase-Initialisierungsfehler');
      }
    };

    checkFirebase();
  }, []);

  useEffect(() => {
    if (!firebaseReady) return;

    let unsubscribeQuizzes;
    let unsubscribeOpenGames;

    const loadData = async () => {
      try {
        // Prüfen ob der User bereits in einer Lobby ist
        if (currentUser) {
        const gamesCollection = collection(db, 'game_sessions');
        const activeGames = query(
          gamesCollection,
          where('gameMasterId', '==', currentUser.uid),
          where('status', 'in', ['waiting', 'selecting_quiz'])
        );
        const snapshot = await getDocs(activeGames);
        if (!snapshot.empty) {
          const lobbyData = snapshot.docs[0];
          setCurrentLobby({ id: lobbyData.id, ...lobbyData.data() });
        } else {
          setCurrentLobby(null);
        }
        }

        // Quizze laden
        const quizzesCollection = collection(db, 'quizzes');
        unsubscribeQuizzes = onSnapshot(quizzesCollection, (snapshot) => {
          const quizList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setQuizzes(quizList);
        });

        // Offene Spiele laden
        const gamesCollection = collection(db, 'game_sessions');
        const openGamesQuery = query(
          gamesCollection, 
          where('status', 'in', ['waiting', 'selecting_quiz'])
        );
        
        unsubscribeOpenGames = onSnapshot(openGamesQuery, async (snapshot) => {
          const openGamesList = await Promise.all(snapshot.docs.map(async (docSnapshot) => {
            const gameData = docSnapshot.data();
            let currentQuiz = gameData.currentQuiz || {};
            
            if (currentQuiz.quizId) {
              try {
                const quizRef = doc(db, 'quizzes', currentQuiz.quizId);
                const quizDoc = await getDoc(quizRef);
                if (quizDoc.exists()) {
                  currentQuiz = {
                    ...currentQuiz,
                    title: quizDoc.data().title
                  };
                }
              } catch (error) {
                console.error('Fehler beim Laden des Quiz-Titels:', error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...gameData,
              currentQuiz
            };
          }));
          
          setOpenGames(openGamesList);
          setLoading(false);
        });

      } catch (error) {
        console.error("Error setting up listeners:", error);
        setError('Fehler beim Laden der Daten: ' + error.message);
        setLoading(false);
      }
    };

    loadData();

    return () => {
      if (unsubscribeQuizzes) unsubscribeQuizzes();
      if (unsubscribeOpenGames) unsubscribeOpenGames();
    };
  }, [firebaseReady, currentUser]);

  const handleQuizSelect = async (quizId) => {
    if (!currentUser) {
      setError('Sie müssen eingeloggt sein, um ein Quiz auszuwählen.');
      return;
    }

    try {
      setLoading(true);

      if (currentLobby) {
        // Quiz in bestehender Lobby ändern
        const sessionRef = doc(db, 'game_sessions', currentLobby.id);
        await updateDoc(sessionRef, {
          'currentQuiz.quizId': quizId,
          'currentQuiz.currentQuestion': 0,
          'currentQuiz.startedAt': null,
          'currentQuiz.endedAt': null,
          status: 'selecting_quiz',
          lastActivity: serverTimestamp()
        });
        navigate(`/lobby/${currentLobby.id}`);
      } else {
        // Neue Lobby erstellen
        const newLobbyId = generateLobbyCode();
        const gameSessionRef = doc(collection(db, 'game_sessions'), newLobbyId);
        await setDoc(gameSessionRef, {
          status: 'selecting_quiz',
          createdAt: serverTimestamp(),
          gameMasterId: currentUser.uid,
          currentQuiz: {
            quizId: quizId,
            currentQuestion: 0,
            startedAt: null,
            endedAt: null
          },
          players: {
            [currentUser.uid]: {
              displayName: currentUser.displayName || 'Unbenannter Spieler',
              photoURL: currentUser.photoURL,
              isReady: false,
              score: 0,
              answers: [],
              correctAnswers: 0,
              isGameMaster: true,
              lastActive: serverTimestamp()
            }
          },
          lastActivity: serverTimestamp()
        });
        navigate(`/lobby/${newLobbyId}`);
      }
    } catch (error) {
      console.error('Error handling quiz selection:', error);
      setError('Fehler beim Auswählen des Quiz');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        position: 'absolute',
        top: { xs: '80px', sm: '90px' },
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        <LinearProgress 
          sx={{ 
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, var(--color-secondary) 0%, var(--color-secondary-light) 100%)'
            }
          }}
        />
      </Box>
    );
  }

  if (error) return (
    <Alert 
      severity="error" 
      sx={{ 
        background: 'rgba(211, 47, 47, 0.1)',
        border: '1px solid rgba(211, 47, 47, 0.3)',
        backdropFilter: 'blur(8px)'
      }}
    >
      {error}
    </Alert>
  );

  // Wenn nicht eingeloggt, Landing Page anzeigen
  if (!currentUser) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: '1500px !important', mx: 'auto' }}>
        <Box sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 4, md: 6 },
          py: { xs: 12, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}>
          <LandingHero />
          <FeatureGrid />
          <Testimonials />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ maxWidth: '1500px !important', mx: 'auto' }}>
      <Box sx={{ py: 4 }}>
        <Box className="section-header" sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                component="h1" 
                className="section-title"
                sx={{ fontSize: '2.5rem !important' }}
              >
                Meine Quizze
              </Typography>
              <Typography className="section-subtitle">
                Verwalten Sie Ihre erstellten Quizze
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/quiz/create')}
                color="primary"
              >
                Quiz erstellen
              </Button>
              <Button
                variant="contained"
                startIcon={<LibraryBooksIcon />}
                onClick={() => navigate('/my-quizzes')}
              >
                Meine Quizze
              </Button>
            </Box>
          </Box>
        </Box>
        <QuizGrid quizzes={quizzes.filter(quiz => quiz.createdBy === currentUser.uid)} onQuizSelect={handleQuizSelect} showEditControls />

        {openGames.length > 0 && (
          <>
            <Box className="section-header">
              <Typography 
                component="h1" 
                className="section-title"
                sx={{ fontSize: '2.5rem !important' }}
              >
                Offene Spiele
              </Typography>
              <Typography className="section-subtitle">
                Tritt einem laufenden Spiel bei oder erstelle ein neues Quiz unten.
              </Typography>
            </Box>
            <OpenGames 
              games={openGames} 
              quizCount={quizzes.length}
              totalRounds={quizzes.reduce((sum, quiz) => sum + (quiz.metadata?.timesPlayed || 0), 0)}
              totalQuestions={quizzes.reduce((sum, quiz) => sum + (quiz.questions?.length || 0), 0)}
            />
          </>
        )}

        <Box className="section-header" sx={{ mt: openGames.length > 0 ? 6 : 4 }}>
          <Typography 
            component="h1" 
            className="section-title"
            sx={{ fontSize: '2.5rem !important' }}
          >
            Verfügbare Quizze
          </Typography>
          <Typography className="section-subtitle">
            Wähle ein Quiz aus und starte ein neues Spiel als Game Master.
          </Typography>
        </Box>
        <QuizGrid 
          quizzes={quizzes.filter(quiz => 
            currentUser ? quiz.createdBy !== currentUser.uid : true
          )} 
          onQuizSelect={handleQuizSelect} 
        />

        {currentUser && (
      <Box className="stats-section">
        <Box className="section-header">
          <Typography 
            component="h1" 
            className="section-title"
            sx={{ fontSize: '2.5rem !important' }}
          >
            Statistiken
          </Typography>
          <Typography className="section-subtitle">
            Aktuelle Zahlen und Fakten aus der Quizzler-Community.
          </Typography>
        </Box>
          <Box className="stats-grid">
            {[
              { 
                  value: openGames.length, 
                label: 'Offene Spiele',
                description: 'Aktive Spielrunden zum Beitreten'
              },
              { 
                  value: quizzes.length, 
                label: 'Verfügbare Quizze',
                description: 'Verschiedene Quiz-Kategorien zur Auswahl'
              },
              { 
                  value: quizzes.reduce((sum, quiz) => sum + (quiz.metadata?.timesPlayed || 0), 0), 
                label: 'Gespielte Runden',
                description: 'Insgesamt gespielte Quiz-Runden'
              },
              { 
                  value: quizzes.reduce((sum, quiz) => sum + (quiz.questions?.length || 0), 0), 
                label: 'Fragen gesamt',
                description: 'Verfügbare Fragen in allen Quizzen'
              }
            ].map((stat, index) => (
              <Box 
                key={index}
                className="stat-card glass"
              >
                <Typography className="quiz-grid-stats-number">
                  {stat.value}
                </Typography>
                <Typography className="stat-label">
                  {stat.label}
                </Typography>
                <Typography className="stat-description">
                  {stat.description}
                </Typography>
              </Box>
            ))}
        </Box>
        </Box>
        )}
      </Box>
    </Container>
  );
};

export default Home; 